(ns frontend.ux-overhaul.listing.pipeline-entity.card-actions
  "The status actions in the menu of a card on the Kanban of a pipeline entity
   type: deals, fundraising opportunities, sales opportunities and exit
   opportunities. Each action runs the same code as the toolbar of the viewer of
   the type. The rules for the actions that a status offers are in
   `common.pipeline-status-actions`. This namespace has one handler table, with
   one entry for each type, so that the four types change together."
  (:require
   [common.localization :as loc]
   [common.opensearch.row :as os-row]
   [common.pipeline-status-actions :as pipeline-status-actions]
   [frontend.domain.exit-opportunity.actions :as exit-opportunity-actions]
   [frontend.domain.fundraising-opportunity.actions :as fundraising-opportunity-actions]
   [frontend.domain.sales-opportunity.actions :as sales-opportunity-actions]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.view.modals.curtains.exit-opportunity.lost-curtain :as exit-lost-curtain]
   [frontend.view.modals.curtains.exit-opportunity.on-hold-curtain :as exit-on-hold-curtain]
   [frontend.view.modals.curtains.fundraising-opportunity.lost-curtain :as fundraising-lost-curtain]
   [frontend.view.modals.curtains.fundraising-opportunity.on-hold-curtain :as fundraising-on-hold-curtain]
   [frontend.view.modals.curtains.sales-opportunity.lost-curtain :as sales-lost-curtain]
   [frontend.view.modals.curtains.sales-opportunity.on-hold-curtain :as sales-on-hold-curtain]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.oppo.events :as oppo-events]))

(def ^:private menu-entries
  {:pass        {:label-key  :pass
                 :icon-class "fal fa-ban"}
   :won         {:label-key  :won
                 :icon-class "fal fa-check"}
   :lost        {:label-key  :lost
                 :icon-class "fal fa-ban"}
   :put-on-hold {:label-key  :put-on-hold
                 :icon-class "fal fa-pause"}
   :reactivate  {:label-key  :reactivate
                 :icon-class "fal fa-redo-alt"}
   :portfolio   {:label-key  :portfolio
                 :icon-class "fal fa-check"}})

(defn- curtain-handler
  "Return a handler that opens a curtain for the entity. The curtains read the
   entity from the session by its id."
  [{:keys [modal-key id-key]}]
  (fn [entity]
    (modals/open-modal! modal-key {id-key (:db/id entity)})))

(defn- handlers
  "Return the handlers of each type, from the action id to a function of the
   entity. The entity is in the session when the handler runs. The context holds
   `:on-open-won-selector!`, which opens the selector of the sale agreement of a
   sales opportunity."
  [{:keys [on-open-won-selector!]}]
  {:entity.type/oppo
   {:pass        oppo-events/start-pass!
    :put-on-hold oppo-events/start-put-on-hold!
    :reactivate  (fn [entity]
                   (oppo-events/resurrect! (:db/id entity)))
    :portfolio   oppo-events/start-to-portfolio!}

   :entity.type/fundraising-opportunity
   {:won         (fn [entity]
                   (fundraising-opportunity-actions/won! {:entity entity}))
    :lost        (curtain-handler {:modal-key fundraising-lost-curtain/modal-key
                                   :id-key    :fundraising-opportunity-id})
    :put-on-hold (curtain-handler {:modal-key fundraising-on-hold-curtain/modal-key
                                   :id-key    :fundraising-opportunity-id})
    :reactivate  fundraising-opportunity-actions/resurrect!}

   :entity.type/sales-opportunity
   {:won         on-open-won-selector!
    :lost        (curtain-handler {:modal-key sales-lost-curtain/modal-key
                                   :id-key    :sales-opportunity-id})
    :put-on-hold (curtain-handler {:modal-key sales-on-hold-curtain/modal-key
                                   :id-key    :sales-opportunity-id})
    :reactivate  sales-opportunity-actions/resurrect!}

   :entity.type/exit-opportunity
   {:won         (fn [entity]
                   (exit-opportunity-actions/won! {:entity entity}))
    :lost        (curtain-handler {:modal-key exit-lost-curtain/modal-key
                                   :id-key    :exit-opportunity-id})
    :put-on-hold (curtain-handler {:modal-key exit-on-hold-curtain/modal-key
                                   :id-key    :exit-opportunity-id})
    :reactivate  exit-opportunity-actions/resurrect!}})

(defn supported?
  "True when the entity type has a card menu."
  [entity-type]
  (contains? pipeline-status-actions/entity-types entity-type))

(defn- with-entity!
  "Fetch the entity into the session, then call f with the entity. The curtains
   and the actions read the entity from the session. A new-grid account has no
   business records in the session until they are fetched."
  [entity-id f]
  (entity-scope/hydrate!
   {:entity-id entity-id
    :on-done   (fn []
                 (if-let [entity (session/get-entity entity-id)]
                   (f entity)
                   (logging/warn "The entity is not in the session after the fetch" entity-id)))}))

(defn- menu-item
  [{:keys [entity-id action handler]}]
  (let [{:keys [label-key icon-class]} (get menu-entries action)]
    {:key        action
     :label      (loc/tr label-key)
     :icon-class icon-class
     :on-click   (fn [_event]
                   (with-entity! entity-id handler))}))

(defn card-actions-fn
  "Return a function from a card to the menu items of the card, in the shape of
   `ui.components.radix-ui.dropdown-menu.view`. The function returns nil for a
   read-only user, for a card that has no id, and for a card with an unknown
   status. The context holds `:entity-type` and `:on-open-won-selector!`."
  [{:keys [entity-type] :as context}]
  (let [type-handlers (get (handlers context) entity-type)]
    (fn [card]
      (when-not (session/is-read-only-user?)
        (when-let [entity-id (os-row/entity-id card)]
          (->> (pipeline-status-actions/available-actions
                entity-type
                (pipeline-status-actions/row-status entity-type card))
               (keep (fn [action]
                       (if-let [handler (get type-handlers action)]
                         (menu-item {:entity-id entity-id
                                     :action    action
                                     :handler   handler})
                         (logging/error "A status action has no handler" entity-type action))))
               vec))))))
